<template>
  <div>
    <b-form-textarea v-model="markdown" rows="15"></b-form-textarea>
    <div class="buttons">
      <b-button variant="outline" @click="showModal">Preview</b-button>
      <b-button variant="primary" @click="copyToClipboard">Copy Report to Clipboard</b-button>
    </div>
    <b-modal ok-only size="xl" ref="preview" title="Preview">
      <div v-html="markdownToHtml"></div>
    </b-modal>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { marked } from 'marked';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import useReportDataStore from '@/stores/reportData';
import {
  IterationBurndown, IterationGoal, IterationVelocity, IterationCapacity, IterationRisks,
  IterationUserStories, ChangeRequests, Bugs, Ceremonies,
  Defects,
} from './reportElements';

export default {
  name: 'PMReport',
  props: {
    completedWorkItemsPerPerson: Array,
    iterationBugs: Array,
    iterationCapacities: Object,
    iterationData: Array,
    previousIterationsData: Array,
    iterationDefects: Array,
    iterationRisksData: Array,
    iterationImpediments: Array,
    iterationFeaturesData: Array,
    projectStartDate: Date,
    selectedIteration: Object,
    teamDaysOff: Object,
    timeOnBugs: Number,
    timeOnDefects: Number,
    isExternalReport: Boolean,
  },
  data() {
    return {
      markdown: '# Report',
    };
  },
  methods: {
    copyToClipboard() {
      navigator.clipboard.writeText(this.markdown);
      toast('Copied to clipboard!', {
        type: 'success',
        position: 'bottom-center',
        theme: 'light',
        autoClose: 3000,
      });
    },
    showModal() {
      this.$refs.preview.show();
    },
    generateReport() {
      const iterationBurndown = IterationBurndown(this.reportDataStore);
      const iterationGoal = IterationGoal(this.reportDataStore);
      const iterationVelocity = IterationVelocity(this.iterationData);
      const iterationCapacity = IterationCapacity(
        this.iterationCapacities,
        this.selectedIteration,
        this.teamDaysOff,
      );
      const iterationRisks = IterationRisks(
        this.iterationRisksData,
        this.iterationImpediments,
      );
      const userStories = IterationUserStories(
        this.iterationData,
        this.previousIterationsData,
        this.isExternalReport,
      );
      const changeRequests = ChangeRequests(
        this.iterationData,
        this.previousIterationsData,
        this.isExternalReport,
      );
      const bugs = Bugs(
        this.iterationBugs,
        this.iterationData,
        this.iterationFeaturesData,
        this.timeOnBugs,
      );
      const defects = Defects(
        this.iterationDefects,
        this.iterationData,
        this.iterationFeaturesData,
        this.timeOnDefects,
      );
      const ceremonies = Ceremonies();

      this.markdown = `
[[_TOC_]]

---
# Instructions (delete this section)
## Before the meeting
1. Project Lead and ScrumMaster gather their thoughts on the sprint.
1. Prepare this report or a powerpoint presentation.

## During the meeting
1. Present a demo of what was achieved. Frontend and Backend.
1. Present the summary of the sprint. (report or pptx)

### Important
- Stay focused on this sprint, overall project status is not the goal of this meeting.
- Gather the action items to help resolve the issues and keep the project going well.
---

# ${this.reportDataStore.iteration.name} Overview
${iterationGoal}

## 📃 Action Items from Previous Sprint
### What were the action items from the previous sprint?
> _Copy all the action items listed in the previous report in the table below and indicate the completion status for each one of them._

Previous Action Item | Owner | Completion Status
-|-|-
Item1 | Owner1 | Completed ✅
Item2 | Owner2 | Unfinished ❌

${iterationVelocity}
${iterationCapacity}
${iterationBurndown}

## 📊 Progression
### What are the deliverable dates?
> It's important to have target dates in order to know how much time is remaining.

- UAT YYYY-MM-DD
- Production YYYY-MM-DD

### Project Plan
> Insert a screenshot of the project plan from Excel with data updated to account for the sprint that just finished.

_Screenshot here_

### How on-track are the deliveries?
> Based on the dates and current progression, how realistic are the delivery dates with the current capacity and remaining work?

- Comment1
- Comment2

## ⚖️ Budget and Estimates
### What are the gaps (in hours) between the estimates and the actual time spent for the features touched in the sprint?
> Grab a screenshot of the **Feature Progression** page of the _KPIs Advanced_ in PowerBI.
> Identify the gaps and give an explanation for each.

![image.png](/.attachments/image-09e42de1-f8cd-4f05-aa1a-e793d9fa03b7.png)

- IdentifiedGap1
- IdentifiedGap2

### What is the plan to deal with those gaps?
> It's important to plan and action early because we can't come up to a client at the end of a project and ask for something at the last minute.
> Make sure you add items to the Action Items section below.

- Plan1
- Plan2
${iterationRisks}
## ⏲️ Time Validation (Azure DevOps vs Harvest)
> Insert a screenshot from PowerBI (KPIs Advanced) using the Sprint Hours report.

![==image_0==.png](/.attachments/==image_0==-598789fd-0a11-4e93-b547-9d86979c1f40.png) 

## 🚀 Action Items
### IMPORTANT - What are the action items to address the issues from this sprint?
> Based on the previous points, list the action items that need to be done in the next sprint.
> Example: Negotiate budget or scope adjustments based on blockers. Adjust the tactical estimates based on new scope.
> Example: Correct the time entries to have exact match between Azure DevOps tasks and Harvest.
> Example: Add the missing impediments.

Action Item | Owner
-|-
item1 | Tag_someone_using_@
item2 | Tag_someone_using_@

---
# Detailed data
**No need to present below this line**

ScrumMaster and Project leads should use the data and questions below as a guide during their preparation.


${userStories}
${changeRequests}
${bugs}
${defects}
${ceremonies}
`;
    },
  },
  created() {
    this.generateReport();
  },
  computed: {
    ...mapStores(useReportDataStore),
    markdownToHtml() {
      return marked(this.markdown);
    },
  },
};
</script>

<style>
.buttons {
  display: flex;
  margin-top: 1rem;
  justify-content: flex-end;
}
.buttons button {
  margin-left: 1rem;
}
</style>
